import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useMutation } from "@apollo/react-hooks";
import { gql } from "apollo-boost";
import ImageUpload from "./components/ImageUpload";
import ProductBasicInfo from "./components/ProductBasicInfo";
import ProductPriceInfo from "./components/ProductPriceInfo";
import ProductDetailInfo from "./components/ProductDetailInfo";
import UploadHeader from "./components/UploadHeader";
import UploadFooter from "./components/UploadFooter";
import MountedWrapper from "../../component/mountedWrapper/MountedWrapper";
import { useAuthStore } from "../../store/useAuthStore";
import { loginUrl, myPageUrl } from "../../util/url";
import "./Upload.css";
import Loading from "../../component/loading/Loading";
import { getGraphQLErrorMsg } from "../../util/util";
import FeeTypeSelector from "./components/FeeTypeSelector";

// Gender 상수 정의
export const PRODUCT_GENDER = {
  UNISEX: "UNISEX",
  MEN: "MEN",
  WOMEN: "WOMEN",
};

export const PRODUCT_GENDER_OBJ = {
  "남성": PRODUCT_GENDER.MEN,
  "여성": PRODUCT_GENDER.WOMEN,
};

// Condition 상수 정의
export const PRODUCT_CONDITION = {
  NEW: "new",
  GOOD_CONDITION: "good_condition",
  LIGHTLY_WORN: "lightly_worn",
  WORN: "worn",
};

// 상품 상태 매핑
export const PRODUCT_CONDITION_OBJ = {
  "새상품": PRODUCT_CONDITION.NEW,
  "아주 좋은 상태": PRODUCT_CONDITION.GOOD_CONDITION,
  "약간의 사용감": PRODUCT_CONDITION.LIGHTLY_WORN,
  "사용감 있음": PRODUCT_CONDITION.WORN,
};

const convertGender = (gender) => {
  return PRODUCT_GENDER_OBJ[gender] || PRODUCT_GENDER.UNISEX;
};

const convertCondition = (condition) => {
  return PRODUCT_CONDITION_OBJ[condition] || PRODUCT_CONDITION.NEW;
};

const POST_UPLOAD_PRODUCT = gql`
  mutation uploadProduct(
    $files: [Upload!]!
    $gender: Gender!
    $category: String!
    $subcategory: String
    $title: String!
    $description: String
    $brand: String
    $size: String
    $condition: Condition
    $price: Int!
    $shipping_fee: Int!
    $shipping_fee_island: Int!
    $is_negotiation: Boolean
    $fee_assigned: String
  ) {
    uploadProduct(
      files: $files
      gender: $gender
      category: $category
      sub_category: $subcategory
      title: $title
      description: $description
      brand: $brand
      size: $size
      condition: $condition
      price: $price
      shipping_fee: $shipping_fee
      shipping_fee_island: $shipping_fee_island
      is_negotiation: $is_negotiation
      fee_assigned: $fee_assigned
    ) {
      id
    }
  }
`;

/**
 * 상품 업로드 기능 추가
 * @returns {Element}
 * @constructor
 */
const Upload = () => {
  const { isLogin, user } = useAuthStore();
  const [productImages, setProductImages] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    trigger,
  } = useForm({
    defaultValues: {
      isNegotiable: true,
      fee_assigned: "SELLER",
      shipping_fee: 0,
      shipping_fee_island: 0,
    },
  });

  const [uploadProduct] = useMutation(POST_UPLOAD_PRODUCT);

  // 로그인 체크
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!isLogin) {
        alert("로그인 후 이용가능합니다.");
        window.location.href = loginUrl();
      } else if (!user.seller.is_tester) {
        alert("PC 업로드 기능을 사용할 수 없습니다.");
        window.location.href = myPageUrl();
      }
    }, 3000);
    return () => clearTimeout(timer);
  }, [isLogin, user]);

  useEffect(() => {
    const originalStyle = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = originalStyle;
    };
  }, []);

  const onSubmit = async (data) => {
    if (!productImages || productImages.length === 0) {
      alert("최소 1개 이상의 상품 이미지를 등록해주세요.");
      return;
    }

    setIsUploading(true);
    try {
      const variables = {
        files: productImages,
        gender: convertGender(data.gender).toUpperCase(),
        category: data.category,
        subcategory: data.subCategory,
        title: data.title,
        description: data.description,
        brand: data.brand,
        size: data.size,
        condition: convertCondition(data.condition).toUpperCase(),
        price: parseInt(data.price),
        shipping_fee: parseInt(data.shipping_fee) || 0,
        shipping_fee_island: parseInt(data.shipping_fee_island) || 0,
        is_negotiation: data.isNegotiable,
        fee_assigned: data.fee_assigned,
      };
      const { data: uploadData } = await uploadProduct({
        variables,
      });
      const productId = parseInt(uploadData.uploadProduct.id, 10);
      const encodeProductId = encodeURIComponent(productId.toString(36));

      window.location.href = `/product/${encodeProductId}`;
    } catch (error) {
      if (error && error.message) {
        alert(getGraphQLErrorMsg(error.message));
      } else {
        alert("상품 등록 중 오류가 발생했습니다.");
      }
    } finally {
      setIsUploading(false);
    }
  };

  const onError = (errors) => {
    console.log("Form validation errors:", errors); // 폼 유효성 검사 에러 로그
  };

  const handleImagesChange = (images) => {
    setProductImages(images);
  };

  return (
    <MountedWrapper>
      <div className="upload-container">
        {isUploading && (
          <div className="upload-loading-overlay">
            <Loading />
          </div>
        )}
        <form
          onSubmit={handleSubmit(onSubmit, onError)}
          className="upload-form"
        >
          <div className="upload-header-wrapper">
            <UploadHeader />
          </div>

          <div className="upload-content-container">
            <div className="upload-left-column">
              <div className="form-group">
                <ImageUpload
                  register={register}
                  onImagesChange={handleImagesChange}
                />
              </div>
            </div>

            <div className="upload-right-column">
              <ProductBasicInfo
                register={register}
                watch={watch}
                errors={errors}
                trigger={trigger}
                setValue={setValue}
              />

              <ProductDetailInfo
                register={register}
                watch={watch}
                setValue={setValue}
                errors={errors}
              />

              <ProductPriceInfo register={register} errors={errors} />

              <FeeTypeSelector register={register} watch={watch} feeWavedUntil={
                user && user.seller && user.seller.feeWavedUntil
                    ? user.seller.feeWavedUntil
                    : undefined
              } />
            </div>
          </div>
          <div className="upload-footer-wrapper">
            <UploadFooter register={register} watch={watch} />
          </div>
        </form>
      </div>
    </MountedWrapper>
  );
};

export default Upload;
